import moment from "moment";
import {
  getQuotesItemsTotal,
  getQuotesTransactionsTotal,
  getNonAdditionalQuotes,
} from "utils/models";

const isNew = (booking) =>
  (!booking.options?.length || booking.options?.length === 0) &&
  (!booking?.quotes?.length || booking?.quotes?.length === 0) &&
  !booking.available &&
  !booking.availabilityCheckSentOn &&
  (!booking.notes || String(booking.notes).trim() === "");

const getTransactionTotal = (booking) =>
  getQuotesTransactionsTotal(getNonAdditionalQuotes(booking?.quotes));

const isOpenEnquiry = (booking) =>
  getTransactionTotal(booking) === 0 && !isNew(booking);

const isOptionsSentNotSeen = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  const transactionTotal = getQuotesTransactionsTotal(nonAdditionalQuotes);
  return (
    transactionTotal === 0 &&
    booking.options?.length > 0 &&
    booking.options.filter((bo) => bo.sentOn).length ===
      booking.options.length &&
    booking.options.filter((bo) => bo.seenOn).length !==
      booking.options.length
  );
};

const isOptionsSeenNotQuoted = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  const transactionTotal = getQuotesTransactionsTotal(nonAdditionalQuotes);
  return (
    transactionTotal === 0 &&
    !booking.available &&
    booking.options?.length > 0 &&
    booking.options.filter((bo) => bo.seenOn).length ===
      booking.options.length
  );
};

const isQuoteNotSeen = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  return (
    nonAdditionalQuotes.length > 0 &&
    nonAdditionalQuotes.filter(
      (q) => q.seenOn || (q.transactions || []).length > 0
    ).length !== nonAdditionalQuotes.length
  );
};

const isQuoteSeenNotPaid = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  return (
    nonAdditionalQuotes.filter(
      (q) => q.seenOn && (q.transactions || []).length === 0
    ).length > 0
  );
};

const hasQuotePayment = (booking) => getTransactionTotal(booking) > 0;

const isQuoteExpired = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  const transactionTotal = getQuotesTransactionsTotal(nonAdditionalQuotes);
  const itemTotal = getQuotesItemsTotal(nonAdditionalQuotes);
  return (
    nonAdditionalQuotes.length > 0 &&
    transactionTotal < itemTotal &&
    nonAdditionalQuotes.filter((q) => q.expired).length ===
      nonAdditionalQuotes.length
  );
};

const isLatePayment = (booking) => {
  const nonAdditionalQuotes = getNonAdditionalQuotes(booking?.quotes);
  const itemTotal = getQuotesItemsTotal(nonAdditionalQuotes);
  const transactionTotal = getQuotesTransactionsTotal(nonAdditionalQuotes);
  return (
    transactionTotal > 0 &&
    transactionTotal < itemTotal &&
    moment(booking.cruiseDate).isBefore(moment().add(2, "weeks"))
  );
};

const withWarningClass = (list) =>
  list.map((b) => ({
    ...b,
    className: moment(b.cruiseDate).isBefore(moment().add(2, "weeks"))
      ? "warning"
      : "",
  }));

export function getBookingFilters(bookings) {
  return [
    {
      title: "New",
      type: "new",
      group: "new",
      tooltip: "A new booking that has not had any interaction",
      bookings: bookings.filter(isNew),
    },
    {
      title: "Open Enquiries",
      type: "open_enquiries",
      group: "new",
      tooltip: "Everything that is not new and not a closed sale",
      bookings: bookings.filter(isOpenEnquiry),
    },
    {
      title: "Options sent, not seen",
      type: "options_sent",
      group: "options",
      tooltip:
        "Options sent are bookings that have had all their options sent out but not seen",
      bookings: bookings.filter(isOptionsSentNotSeen),
    },
    {
      title: "Options seen, not quoted",
      type: "options_seen",
      group: "options",
      tooltip:
        "Options seen are bookings that have had all their options sent out to the customer and seen but no quote",
      bookings: bookings.filter(isOptionsSeenNotQuoted),
    },
    {
      title: "Quote not seen",
      type: "quote_not_seen",
      group: "quote_start",
      tooltip: "Bookings that has a quote that has not been seen",
      bookings: bookings.filter(isQuoteNotSeen),
    },
    {
      title: "Quote seen, not paid",
      type: "quote_seen",
      group: "quote_start",
      tooltip:
        "Bookings that has a quote that has been seen but no transactions",
      bookings: bookings.filter(isQuoteSeenNotPaid),
    },
    {
      title: "Quote with payments",
      type: "quote_with_payments",
      group: "quote",
      tooltip: "Bookings that have had a payment (deposit or full)",
      bookings: withWarningClass(bookings.filter(hasQuotePayment)),
    },
    {
      title: "Quote Expired",
      type: "quote_expired",
      group: "quote",
      tooltip: "Bookings that have all expired quotes",
      bookings: bookings.filter(isQuoteExpired),
    },
    {
      title: "Late Payments",
      type: "late_payments",
      group: "important",
      tooltip: "Bookings that have paid deposit but late paying full",
      bookings: withWarningClass(bookings.filter(isLatePayment)),
    },
    {
      title: "Up Coming",
      type: "up_coming",
      group: "important",
      tooltip: "Bookings coming up",
      bookings: withWarningClass(bookings.filter(hasQuotePayment)),
    },
  ];
}
